// ============================================
// POS HEADER COMPONENT
// Dense, functional header for POS operations
// ============================================

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  LayoutGrid, 
  ShoppingCart, 
  Clock, 
  Pause,
  Package,
  Truck,
  User,
  ChevronDown,
  Settings,
  LogOut,
  ArrowLeft,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

const VIEW_MODES = {
  TABLES: 'tables',
  ORDER: 'order',
  HELD: 'held',
};

export function POSHeader({
  currentTime,
  outlet,
  user,
  viewMode,
  onViewModeChange,
  heldOrdersCount,
  onQuickTakeaway,
  onQuickDelivery,
  isManager,
}) {
  const navigate = useNavigate();

  const formatTime = (date) => {
    return date.toLocaleTimeString('nb-NO', { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('nb-NO', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
  };

  const handleBackToApp = () => {
    navigate('/dashboard');
  };

  return (
    <header 
      data-testid="pos-header"
      className="bg-brand-charcoal text-white px-3 py-2 flex items-center justify-between"
    >
      {/* Left Section - Back Button, Logo & Outlet */}
      <div className="flex items-center gap-3">
        {/* Back Button */}
        <Button
          data-testid="pos-back-button"
          variant="ghost"
          size="sm"
          onClick={handleBackToApp}
          className="text-gray-400 hover:text-white hover:bg-gray-700 gap-1.5 px-2"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="hidden sm:inline text-sm">Exit POS</span>
        </Button>

        {/* Divider */}
        <div className="h-8 w-px bg-gray-700 hidden sm:block" />

        {/* Logo */}
        <div className="flex items-center gap-2">
          <img 
            src="/logo-isf.png" 
            alt="ISF" 
            className="h-8 w-auto"
          />
          <div className="hidden md:block">
            <div className="text-xs text-gray-400">Indian Street Food & Co</div>
            <div className="text-sm font-semibold">{outlet?.name || 'Loading...'}</div>
          </div>
        </div>

        {/* Outlet Badge */}
        <Badge 
          variant="outline" 
          className="bg-gray-800 text-gray-300 border-gray-700 hidden lg:flex"
        >
          {outlet?.code || 'N/A'}
        </Badge>
      </div>

      {/* Center Section - View Mode Tabs */}
      <div className="flex items-center gap-1 bg-gray-800 rounded-lg p-1">
        <button
          data-testid="btn-view-tables"
          onClick={() => onViewModeChange(VIEW_MODES.TABLES)}
          className={cn(
            'flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all',
            viewMode === VIEW_MODES.TABLES
              ? 'bg-brand-orange text-white'
              : 'text-gray-400 hover:text-white hover:bg-gray-700'
          )}
        >
          <LayoutGrid className="w-4 h-4" />
          <span className="hidden sm:inline">Tables</span>
        </button>
        
        <button
          data-testid="btn-view-order"
          onClick={() => onViewModeChange(VIEW_MODES.ORDER)}
          className={cn(
            'flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all',
            viewMode === VIEW_MODES.ORDER
              ? 'bg-brand-orange text-white'
              : 'text-gray-400 hover:text-white hover:bg-gray-700'
          )}
        >
          <ShoppingCart className="w-4 h-4" />
          <span className="hidden sm:inline">Order</span>
        </button>

        <button
          data-testid="btn-view-held"
          onClick={() => onViewModeChange(VIEW_MODES.HELD)}
          className={cn(
            'flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all relative',
            viewMode === VIEW_MODES.HELD
              ? 'bg-brand-orange text-white'
              : 'text-gray-400 hover:text-white hover:bg-gray-700'
          )}
        >
          <Pause className="w-4 h-4" />
          <span className="hidden sm:inline">Held</span>
          {heldOrdersCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
              {heldOrdersCount}
            </span>
          )}
        </button>
      </div>

      {/* Right Section - Quick Actions & User */}
      <div className="flex items-center gap-3">
        {/* Quick Order Buttons */}
        <div className="hidden xl:flex items-center gap-2">
          <Button
            data-testid="header-btn-takeaway"
            variant="outline"
            size="sm"
            onClick={onQuickTakeaway}
            className="bg-transparent border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white gap-1.5"
          >
            <Package className="w-4 h-4" />
            <span>Takeaway</span>
          </Button>
          <Button
            data-testid="header-btn-delivery"
            variant="outline"
            size="sm"
            onClick={onQuickDelivery}
            className="bg-transparent border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white gap-1.5"
          >
            <Truck className="w-4 h-4" />
            <span>Delivery</span>
          </Button>
        </div>

        {/* Time Display */}
        <div className="flex flex-col items-end text-right">
          <div className="flex items-center gap-1.5 text-white font-mono text-lg">
            <Clock className="w-4 h-4 text-gray-400" />
            {formatTime(currentTime)}
          </div>
          <div className="text-xs text-gray-500">
            {formatDate(currentTime)}
          </div>
        </div>

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button 
              data-testid="user-menu-trigger"
              className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 rounded-lg px-3 py-2 transition-colors"
            >
              <div className="w-8 h-8 bg-brand-orange rounded-full flex items-center justify-center">
                <User className="w-4 h-4 text-white" />
              </div>
              <div className="hidden md:block text-left">
                <div className="text-sm font-medium">{user?.name?.split(' ')[0] || 'User'}</div>
                <div className="text-xs text-gray-400">
                  {isManager ? 'Manager' : 'Staff'}
                </div>
              </div>
              <ChevronDown className="w-4 h-4 text-gray-400" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem className="gap-2">
              <User className="w-4 h-4" />
              {user?.name || 'Unknown User'}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {isManager && (
              <DropdownMenuItem className="gap-2">
                <Settings className="w-4 h-4" />
                POS Settings
              </DropdownMenuItem>
            )}
            <DropdownMenuItem className="gap-2 text-red-600">
              <LogOut className="w-4 h-4" />
              Lock POS
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
